// @flow
import type { CompanyBranch } from "../../../lib/Types";
import "react-dates/initialize";
import "react-dates/lib/css/_datepicker.css";
import React from "react";
import { MinimalContainer } from "../Theme";
import styled, { css } from "styled-components";
import Input from "../../Input";
import Checkbox from "../../common/form/Checkbox";
import Button from "../../Button";
import { DateRangePicker } from "react-dates";
import { fetchJson } from "../../../lib/Fetching";
import moment from "moment";
import CompanyBranchDropDown from "../../crm/CompanyBranchDropDown";
import DataFieldFilter from "../DataFieldFilter";
import CompanyDropDown from "../../crm/CompanyDropDown";
import DataFieldsDropDown from "../DataFieldsDropDown";
import MatrixInput from "../../testing/MatrixInput";
import ClientSuiteInput from "../../products/ClientSuiteInput";
import TestingCategorySelect from "../../sample_reception/TestingCategorySelect";

type Props = {
  company_id: ?number
};

type State = {
  company_id: ?number,
  companyBranch: ?CompanyBranch,
  loading: boolean,
  product_name: ?string,
  start_date: ?Object,
  end_date: ?Object,
  startDateFocused: boolean,
  endDateFocused: boolean,
  matrixId: ?number,
  clientSuiteId: ?number
};

const Container = styled(MinimalContainer)`
  padding: 20px;
  max-width: 1920px;
  margin: 0 auto;
`;

const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin: 0 -15px;
`;

const Column = styled.div`
  flex-grow: 1;
  min-width: 350px;
  padding: 0 15px;
  margin-bottom: 5px;
`;

const FullColumn = styled.div`
  width: 100%;
  padding: 0 15px;
`;
export default class ProductFailureSampleSearch extends React.Component<Props, State> {
  state = {
    company_id: this.props.company_id,
    companyBranch: null,
    loading: false,
    product_name: "",
    start_date: moment().subtract(30, "days"),
    end_date: moment(),
    startDateFocused: false,
    endDateFocused: false,
    exclude_product_name: "",
    clientSuiteId: null
  };

  isValid = (): boolean => {
    const { start_date, end_date } = this.state;
    if (!start_date || !end_date) return false;
    if (end_date < start_date) return false;
    return true;
  };

  loadData = () => {
    const { onLoad } = this.props;
    const {
      company_id,
      product_name,
      exclude_product_name,
      start_date,
      end_date,
      companyBranch,
      matrixId,
      clientSuiteId
    } = this.state;
    this.setState({ loading: true });
    onLoad({ data: null, searchParams: {} });

    const formParams = {
      form: {
        company_ids: [company_id],
        company_branch_ids: companyBranch ? [companyBranch.id] : null,
        product_name,
        exclude_product_name,
        start_date: start_date ? start_date.format("YYYY-MM-DD") : null,
        end_date: end_date ? end_date.format("YYYY-MM-DD") : null,
        data_fields: this.dataFieldFilter ? this.dataFieldFilter.queryParams() : [],
        matrix_id: matrixId,
        client_suite_id: clientSuiteId
      }
    };

    fetchJson("/sample_reporting/micro_summary.json", formParams)
      .then(json => {
        this.setState({ loading: false });
        onLoad({ data: json, searchParams: formParams.form });
      })
      .catch(e => {
        this.setState({ loading: false });
        alert("There was a problem loading this data.");
        throw e;
      });
  };

  branchChanged = (companyBranch: CompanyBranch) => {
    this.setState({
      companyBranch
    });
  };

  render() {
    const {
      company_id,
      loading,
      product_name,
      exclude_product_name,
      start_date,
      end_date,
      startDateFocused,
      endDateFocused,
      companyBranch,
      selectedBreakdownDataFields,
      testingCategory,
      clientSuiteId
    } = this.state;
    return (
      <Container className="hidden-print">
        <div className="basic-panel" style={{ marginBottom: 20, padding: 10 }}>
          Only samples and results that are associated with a specification given by the client
          are shown in this report. This mainly applies to Micro testing.
        </div>
        <Row>
          <Column>
            <label>{I18n.t("registered_between")}</label>
            <br />
            <DateRangePicker
              startDateId="start_date"
              startDate={start_date}
              endDateId="end_date"
              endDate={end_date}
              numberOfMonths={1}
              onDatesChange={({ startDate, endDate }) =>
                this.setState({ start_date: startDate, end_date: endDate })
              }
              focusedInput={this.state.focusedInput || null}
              onFocusChange={focusedInput => this.setState({ focusedInput })}
              showClearDates={true}
              isOutsideRange={() => false}
              hideKeyboardShortcutsPanel={true}
              displayFormat="DD MMM YYYY"
            />
          </Column>
          {company_id && (
            <Column>
              <label>{I18n.t("select_branch")}</label>
              <CompanyBranchDropDown
                key={company_id}
                company_id={company_id}
                onBranchChange={this.branchChanged}
                value={companyBranch ? companyBranch.id : null}
                includeBlank={true}
                blankLabel={"All Branches"}
                permission_code={"view_samples"}
              />
            </Column>
          )}
          {company_id && (
            <Column>
              <label>{I18n.t("client_suite")}</label>
              <ClientSuiteInput
                company_id={company_id}
                multiple={false}
                value={clientSuiteId}
                onChange={option =>
                  this.setState({ clientSuiteId: option ? option.value : null })
                }
              />
            </Column>
          )}
          <Column>
            <label>{I18n.t("product_name")}</label>
            <Input
              focus
              onPressEnter={this.loadData}
              className="form-control"
              value={product_name}
              onChange={e => this.setState({ product_name: e.target.value })}
            />
          </Column>
          <Column>
            <label>{I18n.t("exclude_product_name")}</label>
            <Input
              focus
              onPressEnter={this.loadData}
              className="form-control"
              value={exclude_product_name}
              onChange={e => this.setState({ exclude_product_name: e.target.value })}
              placeholder={
                "e.g. add 'Shelf,Wash' to ignore products with 'Shelf' & 'Wash' in name"
              }
            />
          </Column>
        </Row>
        {company_id && (
          <Row style={{ paddingBottom: 0, paddingTop: 5 }}>
            <FullColumn>
              <DataFieldFilter
                key={company_id}
                ref={ref => (this.dataFieldFilter = ref)}
                onSubmit={this.loadData}
                company_id={company_id}
                company_branch_id={companyBranch ? companyBranch.id : null}
              />
            </FullColumn>
          </Row>
        )}
        <Row>
          <Column style={{ marginBottom: 0, paddingBottom: 0 }}>
            <Button
              disabled={!this.isValid()}
              title={loading ? I18n.t("loading") : I18n.t("view_summary")}
              icon="fa-search"
              onClick={this.loadData}
            />
          </Column>
        </Row>
      </Container>
    );
  }
}
